import { useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSEO } from "../lib/seo";
import { Check, ArrowRight, Star, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PLANS = [
  {
    id: "monthly",
    name: "Pro Monthly",
    price: 19,
    interval: "/month",
    features: [
      "Access to all 50+ practice tests",
      "Adaptive analytics & readiness score",
      "Streaks, XP and badges",
      "30-day money-back",
    ],
  },
  {
    id: "annual",
    name: "Pro Annual",
    price: 149,
    interval: "/year",
    badge: "Most popular",
    highlight: true,
    features: [
      "Everything in Pro Monthly",
      "Save $79/year vs. monthly",
      "Priority support",
      "Free new course access",
      "Verified completion certificates",
    ],
  },
  {
    id: "lifetime",
    name: "Lifetime",
    price: 399,
    interval: "one-time",
    features: [
      "Lifetime access to every course",
      "Every future cert & update",
      "Early access to new features",
      "Founders' Slack community",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  useSEO({
    title: "Pricing — Pro, Annual & Lifetime plans",
    description:
      "Simple, transparent pricing. Pro Monthly $19, Pro Annual $149 (save $79), Lifetime $399. 30-day money-back. Pass one cert and the math is easy.",
  });

  const buyPlan = async (plan) => {
    if (!user) { navigate("/login", { state: { from: { pathname: "/pricing" } } }); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout/plan", {
        plan_id: plan.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div data-testid="pricing-page">
      <section className="border-b border-border hero-mesh relative">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-x relative py-16 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/70 backdrop-blur px-3 py-1 text-xs font-semibold mx-auto">
            <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-blue)]" /> Save up to 40% on Annual
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter">Plans that pay for themselves.</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Pass one certification. Earn a $20k raise. The math is easy.
          </p>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className={`rounded-2xl border p-7 flex flex-col relative card-hover ${
                p.highlight
                  ? "border-[color:var(--brand-gold)] bg-card ring-gold"
                  : "border-border bg-card"
              }`}
              data-testid={`plan-${p.id}`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-7 rounded-md bg-[color:var(--brand-gold)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1f1300]">
                  {p.badge}
                </div>
              )}
              <div className="font-display text-2xl tracking-tight">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl tracking-tighter">${p.price}</span>
                <span className="text-sm text-muted-foreground">{p.interval}</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 text-[color:var(--brand-success)]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buyPlan(p)}
                disabled={busy === p.id}
                className={`mt-6 h-12 w-full font-semibold ${
                  p.highlight
                    ? "btn-gold !w-full"
                    : "bg-foreground text-background hover:opacity-90"
                }`}
                data-testid={`buy-plan-${p.id}`}
              >
                {busy === p.id ? "Redirecting…" : <>Get {p.name} <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="flex items-center justify-center gap-1 mb-2">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-500 stroke-yellow-500" />)}
          </div>
          <p className="text-sm text-muted-foreground">"BeforeExam paid for itself before I'd even taken the exam." — Cloud Engineer @ Stripe</p>
        </div>
      </section>
    </div>
  );
}
